import React, { FC } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import { IconButton } from "@natera/material/lib/button";
import { Button } from "@natera/form";
import Close from "@assets/svg/icons/close.svg";
import { routes } from "@app/routing";
import { ONCOLOGY_TEST_TYPES, TestType } from "@app/provider/testData/types";
import { GIS_NO_ORDER_UID } from "@app/utils/gisEntrySource";
import { useGisGuest } from "@app/pages/private/scheduleAGeneticInformationSession/context";
import { UnderstandingYourResultsOncologyContent } from "@app/pages/private/testResult/understandingYourResults/contentType";

import "@app/pages/private/testResult/understandingYourResults/understandingYourResults.scss";
import "./guestUnderstandingResults.scss";

const messages = defineMessages({
  understandingYourResultsHeadline: {
    id: "understandingYourResultsHeadline",
    defaultMessage: "Understanding Your Results",
  },
  understandingYourResultsCloseLabel: {
    id: "understandingYourResultsCloseLabel",
    defaultMessage: "Close",
  },
  understandingYourResultsBackButton: {
    id: "understandingYourResultsBackButton",
    defaultMessage: "Back",
  },
});

/**
 * Guest counterpart to the authenticated `UnderstandingYourResultsPage`,
 * reached from the GIS patient-education step of the anonymous order-lookup
 * funnel.
 *
 * Guests have no test card and no access to the result document, so this
 * renders only the static, test-type-keyed explanation of what each result
 * means and returns to the education step instead of opening the report.
 */
const GuestUnderstandingResults: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { orderUidPublic, testUid } = useParams<{
    orderUidPublic: string;
    testUid: string;
  }>();
  const { testType } = useGisGuest();

  const educationRoute =
    routes.scheduleAGeneticInformationSessionPatientEducationPage(
      orderUidPublic ?? "",
      testUid ?? ""
    );

  // The explainer covers the results of a test the visitor has, so it's offered
  // only to guests whose order was found. Of those, only oncology tests have
  // this content; the rest are linked from the education step as external
  // articles instead.
  if (
    orderUidPublic === GIS_NO_ORDER_UID ||
    !ONCOLOGY_TEST_TYPES.includes(testType as TestType)
  ) {
    return <Navigate to={educationRoute} replace />;
  }

  const goBack = () => navigate(educationRoute);

  return (
    <div
      className="understanding-your-results guest-understanding-your-results"
      data-testid="guest-understanding-your-results"
    >
      <div className="understanding-your-results__header">
        <h2>{intl.formatMessage(messages.understandingYourResultsHeadline)}</h2>
        <IconButton
          onClick={goBack}
          aria-label={intl.formatMessage(
            messages.understandingYourResultsCloseLabel
          )}
        >
          {Close}
        </IconButton>
      </div>
      <section className="understanding-your-results__container">
        <div className="understanding-your-results__container__header">
          <h3 className="understanding-your-results__container__title">
            {intl.formatMessage(messages.understandingYourResultsHeadline)}
          </h3>
          <div className="understanding-your-results__container__buttons-wrapper">
            <Button onClick={goBack} data-automation_id="back_button">
              {intl.formatMessage(messages.understandingYourResultsBackButton)}
            </Button>
          </div>
        </div>
        <UnderstandingYourResultsOncologyContent
          testType={testType as TestType}
        />
        <div className="understanding-your-results__container__footer">
          <Button
            className="understanding-your-results__container__action-button"
            raised
            onClick={goBack}
            data-automation_id="back_button"
          >
            {intl.formatMessage(messages.understandingYourResultsBackButton)}
          </Button>
        </div>
      </section>
    </div>
  );
};

export default GuestUnderstandingResults;
